import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import moment from 'moment-timezone';

import { useTeamMemberDetailsForSelect } from '@hooks/useTeamMemberDetailsForSelect';
import { useMemberTimezone } from '@hooks/useMemberTimezone';
import alertsService from '@services/alerts';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import { useFocusVsDistraction } from '@hooks/useFocusVsDistraction';
import { queryKeys } from '@config/queryKeys';
import { fetchConsolidatedDay } from '@services/teamConsolidated';
import {
  fetchMemberAccumulator,
  fetchMemberDayData,
  fetchMemberRecentDays,
  normalizeMemberDay,
} from '@services/memberConsolidated';
import {
  buildUserOverallProductivity,
  buildUserKeyHighlights,
  buildUserProductivityOverTime,
  buildUserChartTicks,
  buildUserActivityBreakdown,
  buildUserTopApplications,
  buildUserAttendanceSummary,
  buildUserActivityHeatmap,
} from '../DashboardOverviewNew.helpers';

export function useDashboardData() {
  const team = useSelector((state) => state.team);
  const teamId = team?.active?.activeTeamid;
  const adminUID = useSelector((s) => s.auth?.profile?.id);
  const companyId = useSelector(
    (s) => s.auth.profile?.activeCompany?.companyId
  );

  // ── Member list ──────────────────────────────────────────────────────────
  const { memberDetails, isLoading: isLoadingMembers } =
    useTeamMemberDetailsForSelect(teamId);

  const membersOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails)
      .filter(([, m]) => !m?.deleted)
      .map(([uid, m]) => ({
        value: uid,
        label: m.employeeId
          ? `${m.userName || uid} (${m.employeeId})`
          : m.userName || uid,
        employeeId: m.employeeId,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [memberDetails]);

  // ── Filters ───────────────────────────────────────────────────────────────
  const todayStr = moment().format('YYYY-MM-DD');
  const [dateRange, setDateRange] = useState({
    start: todayStr,
    end: todayStr,
  });
  const [selectedMember, setSelectedMember] = useState(null);

  // Auto-select first member once the roster loads
  useEffect(() => {
    if (membersOptions.length > 0 && !selectedMember) {
      setSelectedMember(membersOptions[0]);
    }
  }, [membersOptions, selectedMember]);

  // ── Raw Firestore data ────────────────────────────────────────────────────
  const [rawToday, setRawToday] = useState(null);
  const [rawYesterday, setRawYesterday] = useState(null);
  const [rawLast7, setRawLast7] = useState([]);
  const [rawTeamDay, setRawTeamDay] = useState(null);
  const [rawAlerts, setRawAlerts] = useState(null);

  // ── Loading flags ─────────────────────────────────────────────────────────
  const [isFetchingStats, setIsFetchingStats] = useState(false);
  const [isFetchingAlerts, setIsFetchingAlerts] = useState(false);

  const { data: appNameMapping = {} } = useQuery({
    queryKey: queryKeys.applicationInfo.mapping,
    queryFn: fetchApplicationInfoMapping,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  // ── Fetch callbacks ───────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (!selectedMember?.value || !teamId) return;
    setRawToday(null);
    setRawYesterday(null);
    setRawLast7([]);
    setRawTeamDay(null);
    setIsFetchingStats(true);
    try {
      const selectedDate = dateRange.start;
      const prevDate = moment(selectedDate)
        .subtract(1, 'day')
        .format('YYYY-MM-DD');
      const userId = selectedMember.value;

      const [accDoc, yesterdayDoc, recentDocs, teamDayDoc] = await Promise.all([
        fetchMemberAccumulator(teamId, userId),
        fetchMemberDayData(teamId, userId, prevDate),
        fetchMemberRecentDays(teamId, userId, 7),
        fetchConsolidatedDay(teamId, selectedDate),
      ]);

      // Today's live data lives in the accumulator's day section
      const accDaySection = accDoc?.day;
      const todayNorm =
        accDaySection?.dateId === selectedDate
          ? normalizeMemberDay(accDaySection)
          : null;

      setRawToday(todayNorm);
      setRawYesterday(normalizeMemberDay(yesterdayDoc));

      // Build last-7 for heatmap: merge daysData with today from accumulator
      const normalized = recentDocs.map(normalizeMemberDay).filter(Boolean);
      const hasToday = normalized.some((d) => d.dateId === selectedDate);
      setRawLast7(
        todayNorm && !hasToday ? [...normalized, todayNorm] : normalized
      );

      setRawTeamDay(teamDayDoc);
    } catch (_) {
      // widgets fall back to empty state
    } finally {
      setIsFetchingStats(false);
    }
  }, [selectedMember, dateRange.start, teamId]);

  const fetchAlerts = useCallback(async () => {
    if (!selectedMember?.value || !adminUID) return;
    setRawAlerts(null);
    setIsFetchingAlerts(true);
    try {
      const { success, data } = await alertsService.getMemberAlertsForAdmin(
        adminUID,
        selectedMember.value,
        { limit: 5 }
      );
      if (success) setRawAlerts(data);
    } finally {
      setIsFetchingAlerts(false);
    }
  }, [selectedMember, adminUID]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // ── Derived display data ──────────────────────────────────────────────────
  const timezone = useMemberTimezone(selectedMember?.value);

  const overallProductivityData = useMemo(
    () => buildUserOverallProductivity(rawToday, rawYesterday, rawTeamDay),
    [rawToday, rawYesterday, rawTeamDay]
  );

  const { data: focusVsDistractionData } = useFocusVsDistraction(
    rawToday?.appUsage,
    rawToday?.totalSec,
    { companyId, teamId }
  );

  const keyHighlightsData = useMemo(
    () =>
      buildUserKeyHighlights(
        rawToday,
        rawYesterday,
        rawAlerts,
        focusVsDistractionData
      ),
    [rawToday, rawYesterday, rawAlerts, focusVsDistractionData]
  );

  const productivityOverTimeData = useMemo(
    () => buildUserProductivityOverTime(rawToday, rawTeamDay, timezone),
    [rawToday, rawTeamDay, timezone]
  );

  const chartTicks = useMemo(
    () => buildUserChartTicks(rawToday, timezone),
    [rawToday, timezone]
  );

  const activityBreakdownData = useMemo(
    () => buildUserActivityBreakdown(rawToday),
    [rawToday]
  );

  const topApplicationsData = useMemo(
    () => buildUserTopApplications(rawToday?.appUsage, appNameMapping),
    [rawToday, appNameMapping]
  );

  const recentAlertsData = useMemo(() => {
    if (!rawAlerts) return null;
    return rawAlerts.map((alert) => ({
      id: alert.id,
      iconType: alert.severity === 'low' ? 'bar-chart' : 'triangle',
      iconBg: alert.severity === 'high' ? '#FFF1F3' : '#FFF6ED',
      iconColor: alert.severity === 'high' ? '#ef4444' : '#f59e0b',
      title: alert.title || 'Alert',
      subtitle: alert.metadata?.userEmailId || '',
      time: alertsService.formatDate(alert.createdAt),
    }));
  }, [rawAlerts]);

  const activityHeatmapData = useMemo(
    () => buildUserActivityHeatmap(rawLast7, dateRange.start, timezone),
    [rawLast7, dateRange.start, timezone]
  );

  const attendanceSummaryData = useMemo(
    () => buildUserAttendanceSummary(rawTeamDay),
    [rawTeamDay]
  );

  return {
    // Filters
    dateRange,
    setDateRange,
    membersOptions,
    selectedMember,
    setSelectedMember,
    isLoadingMembers,
    // Loading flags
    isFetchingStats,
    isFetchingAlerts,
    // Display data
    overallProductivityData,
    keyHighlightsData,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    recentAlertsData,
    productivityOverTimeData,
    chartTicks,
    activityHeatmapData,
    attendanceSummaryData,
  };
}
